import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getProductPath } from '../utils/helpers';
import { Package, MapPin, Heart, User, LogOut, ChevronRight, Truck, Clock, CheckCircle, XCircle, Plus, Trash2, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDashboard = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '' });
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [addrForm, setAddrForm] = useState({ label: 'Casa', name: '', address: '', city: '', province: '', zip_code: '', country: 'IT', phone: '' });

  const t = language === 'it';

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchOrders();
      fetchAddresses();
      setProfileForm({ name: user.name || '', phone: user.phone || '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/orders`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch { /* */ } finally { setLoadingData(false); }
  };

  const fetchAddresses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/addresses`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAddresses(data.addresses || []);
      }
    } catch { /* */ }
  };

  const handleSaveProfile = async () => {
    try {
      await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm)
      });
      toast.success(t ? 'Profilo aggiornato' : 'Profile updated');
      setEditProfile(false);
    } catch { toast.error('Errore'); }
  };

  const handleAddAddress = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/addresses`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addrForm)
      });
      if (res.ok) {
        toast.success(t ? 'Indirizzo aggiunto' : 'Address added');
        setShowAddAddress(false);
        setAddrForm({ label: 'Casa', name: '', address: '', city: '', province: '', zip_code: '', country: 'IT', phone: '' });
        fetchAddresses();
      }
    } catch { toast.error('Errore'); }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm(t ? 'Eliminare questo indirizzo?' : 'Delete this address?')) return;
    await fetch(`${API_URL}/api/auth/addresses/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchAddresses();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
    toast.success(t ? 'Disconnesso' : 'Logged out');
  };

  const getStatusInfo = (status) => {
    const map = {
      pending: { label: t ? 'In attesa' : 'Pending', color: '#f59e0b', icon: Clock },
      processing: { label: t ? 'In lavorazione' : 'Processing', color: '#3b82f6', icon: Package },
      shipped: { label: t ? 'Spedito' : 'Shipped', color: '#8b5cf6', icon: Truck },
      delivered: { label: t ? 'Consegnato' : 'Delivered', color: '#22c55e', icon: CheckCircle },
      cancelled: { label: t ? 'Annullato' : 'Cancelled', color: '#ef4444', icon: XCircle },
    };
    return map[status] || map.pending;
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>Caricamento...</div>;
  if (!user) return null;

  const tabs = [
    { id: 'orders', label: t ? 'I Miei Ordini' : 'My Orders', icon: Package },
    { id: 'addresses', label: t ? 'Indirizzi' : 'Addresses', icon: MapPin },
    { id: 'profile', label: t ? 'Profilo' : 'Profile', icon: User },
  ];

  return (
    <div className="min-h-screen pt-20" style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" data-testid="dashboard-title">
              {t ? 'Ciao' : 'Hello'}, {user.name || user.email}!
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{user.email}</p>
          </div>
          <button onClick={handleLogout} className="btn-secondary flex items-center gap-2 text-sm" data-testid="logout-btn">
            <LogOut size={16} /> {t ? 'Esci' : 'Logout'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b" style={{ borderColor: 'var(--border-primary)' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm transition-colors border-b-2 ${
                activeTab === tab.id ? 'text-[var(--brand-primary)] border-[var(--brand-primary)]' : 'border-transparent hover:text-[var(--brand-primary)]'
              }`}
              style={activeTab !== tab.id ? { color: 'var(--text-muted)' } : {}}
            >
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="dark-card p-6">

          {/* === ORDERS === */}
          {activeTab === 'orders' && (
            <div>
              <h2 className="text-xl font-bold mb-4">{t ? 'I Miei Ordini' : 'My Orders'} ({orders.length})</h2>
              {loadingData ? (
                <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>{t ? 'Caricamento...' : 'Loading...'}</p>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                  <p style={{ color: 'var(--text-muted)' }}>{t ? 'Nessun ordine ancora' : 'No orders yet'}</p>
                  <Link to="/products" className="btn-primary inline-block mt-4">{t ? 'Scopri i Prodotti' : 'Browse Products'}</Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => {
                    const status = getStatusInfo(order.status);
                    const StatusIcon = status.icon;
                    return (
                      <div key={order.order_id} className="p-4 rounded-lg" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-secondary)' }} data-testid={`order-${order.order_id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                          <div>
                            <span className="font-mono font-bold text-sm text-[var(--brand-primary)]">{order.order_id}</span>
                            <span className="text-xs ml-3" style={{ color: 'var(--text-muted)' }}>
                              {new Date(order.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: `${status.color}20`, color: status.color }}>
                              <StatusIcon size={14} /> {status.label}
                            </span>
                            <span className="font-bold text-[var(--brand-primary)]">€{order.total?.toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Tracking */}
                        {order.carrier && (
                          <div className="flex items-center gap-2 mb-3 text-sm">
                            <Truck size={14} style={{ color: 'var(--brand-primary)' }} />
                            <span className="font-semibold">{order.carrier}</span>
                            {order.tracking_number && <span className="font-mono" style={{ color: 'var(--text-muted)' }}>{order.tracking_number}</span>}
                          </div>
                        )}

                        {/* Items */}
                        <div className="flex flex-wrap gap-2">
                          {order.items?.slice(0, 4).map((item) => (
                            <span key={item.sku} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg-secondary)' }}>
                              {(item.name_it || item.name || '').substring(0, 30)}{(item.name_it || item.name || '').length > 30 ? '...' : ''} x{item.quantity}
                            </span>
                          ))}
                          {order.items?.length > 4 && <span className="text-xs px-2 py-1" style={{ color: 'var(--text-muted)' }}>+{order.items.length - 4} {t ? 'altri' : 'more'}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* === ADDRESSES === */}
          {activeTab === 'addresses' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">{t ? 'I Miei Indirizzi' : 'My Addresses'}</h2>
                <button onClick={() => setShowAddAddress(!showAddAddress)} className="btn-primary text-sm flex items-center gap-2" data-testid="add-address-btn">
                  <Plus size={16} /> {t ? 'Aggiungi' : 'Add'}
                </button>
              </div>

              {showAddAddress && (
                <div className="mb-6 p-4 rounded-lg" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { key: 'label', label: t ? 'Etichetta (es. Casa, Ufficio)' : 'Label', placeholder: 'Casa' },
                      { key: 'name', label: t ? 'Nome completo' : 'Full name', placeholder: '', required: true },
                      { key: 'address', label: t ? 'Indirizzo' : 'Address', placeholder: '', required: true },
                      { key: 'city', label: t ? 'Citta' : 'City', placeholder: '', required: true },
                      { key: 'province', label: t ? 'Provincia' : 'Province', placeholder: '' },
                      { key: 'zip_code', label: 'CAP', placeholder: '', required: true },
                      { key: 'country', label: t ? 'Paese' : 'Country', placeholder: 'IT' },
                      { key: 'phone', label: t ? 'Telefono' : 'Phone', placeholder: '' },
                    ].map(f => (
                      <div key={f.key}>
                        <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{f.label}</label>
                        <input
                          type="text" value={addrForm[f.key]}
                          onChange={(e) => setAddrForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                          placeholder={f.placeholder}
                          className="w-full rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
                          style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={handleAddAddress} className="btn-primary text-sm px-4">{t ? 'Salva' : 'Save'}</button>
                    <button onClick={() => setShowAddAddress(false)} className="btn-secondary text-sm px-4">{t ? 'Annulla' : 'Cancel'}</button>
                  </div>
                </div>
              )}

              {addresses.length === 0 && !showAddAddress ? (
                <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>{t ? 'Nessun indirizzo salvato' : 'No saved addresses'}</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {addresses.map((addr) => (
                    <div key={addr.id} className="p-4 rounded-lg relative" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-secondary)' }}>
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: 'rgba(255,102,0,0.15)', color: 'var(--brand-primary)' }}>{addr.label}</span>
                        <button onClick={() => handleDeleteAddress(addr.id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 size={14} /></button>
                      </div>
                      <p className="font-semibold text-sm">{addr.name}</p>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{addr.address}</p>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{addr.zip_code} {addr.city} ({addr.province})</p>
                      {addr.phone && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{addr.phone}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === PROFILE === */}
          {activeTab === 'profile' && (
            <div>
              <h2 className="text-xl font-bold mb-4">{t ? 'Il Mio Profilo' : 'My Profile'}</h2>
              <div className="max-w-md space-y-4">
                <div>
                  <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Email</label>
                  <p className="font-mono text-sm p-3 rounded" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}>{user.email}</p>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{t ? 'Metodo di accesso' : 'Login method'}</label>
                  <p className="text-sm p-3 rounded" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}>
                    {user.auth_method === 'google' ? 'Google' : 'Email + Password'}
                  </p>
                </div>

                {editProfile ? (
                  <>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{t ? 'Nome' : 'Name'}</label>
                      <input type="text" value={profileForm.name} onChange={(e) => setProfileForm(p => ({ ...p, name: e.target.value }))}
                        className="w-full rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
                        style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                        data-testid="edit-name" />
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{t ? 'Telefono' : 'Phone'}</label>
                      <input type="tel" value={profileForm.phone} onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                        className="w-full rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
                        style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                        data-testid="edit-phone" />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleSaveProfile} className="btn-primary text-sm px-4">{t ? 'Salva' : 'Save'}</button>
                      <button onClick={() => setEditProfile(false)} className="btn-secondary text-sm px-4">{t ? 'Annulla' : 'Cancel'}</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{t ? 'Nome' : 'Name'}</label>
                      <p className="text-sm p-3 rounded" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}>{user.name || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{t ? 'Telefono' : 'Phone'}</label>
                      <p className="text-sm p-3 rounded" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}>{user.phone || '-'}</p>
                    </div>
                    <button onClick={() => setEditProfile(true)} className="btn-secondary text-sm flex items-center gap-2" data-testid="edit-profile-btn">
                      <Edit2 size={14} /> {t ? 'Modifica Profilo' : 'Edit Profile'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
